import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  createFixedAssetsSpecies,
  deleteFixedAssetsSpecies,
  fetchFixedAssetsSpecies,
  FixedAssetSpeciesCategoryAccountsPayload,
  updateFixedAssetsSpeciesCategoryAccounts,
} from '~/api/bookkeeping/fixed-assets-species';
import { queryClient } from '~/client/queryClient';
import { snack } from '~/hooks/ToastProvider';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import { CompanyFixedAssetSpecies, FixedAssetsSpeciesQueryKeys } from '~/types/fixed-asset';
import { buildSpeciesCategoryIndex } from '~/utils/bookkeeping/fixed-asset-accounts';

export const useFixedAssetsSpeciesQuery = (options: { enabled?: boolean } = {}) => {
  const companyId = useActiveCompanyId();
  return useQuery({
    queryKey: FixedAssetsSpeciesQueryKeys.byCompany(companyId),
    queryFn: () => fetchFixedAssetsSpecies(companyId),
    staleTime: Infinity,
    enabled: options.enabled ?? true,
  });
};

export const useFixedAssetsSpeciesCategoryIndexQuery = (options: { enabled?: boolean } = {}) => {
  const companyId = useActiveCompanyId();
  return useQuery({
    queryKey: FixedAssetsSpeciesQueryKeys.byCompany(companyId),
    queryFn: () => fetchFixedAssetsSpecies(companyId),
    staleTime: Infinity,
    enabled: options.enabled ?? true,
    select: buildSpeciesCategoryIndex,
  });
};

export const useDeleteFixedAssetsSpeciesMutation = () => {
  const companyId = useActiveCompanyId();
  const { captureException } = useExceptionLogger();
  const { t } = useTranslation();
  const { t: tModal } = useTranslation('bookkeeping', { keyPrefix: 'settings.fixed_assets.modal' });

  return useMutation({
    mutationFn: (speciesId: string) => deleteFixedAssetsSpecies(companyId, speciesId),
    onError: (error) => {
      captureException(error);
      snack.error(t('ENCOUNTERED_ERROR'));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FixedAssetsSpeciesQueryKeys.byCompany(companyId) });
      snack.success(tModal('delete_success'));
    },
  });
};

export const useUpdateFixedAssetsSpeciesCategoryAccountsMutation = () => {
  const companyId = useActiveCompanyId();
  const { captureException } = useExceptionLogger();
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'settings.fixed_assets.detail' });

  return useMutation({
    mutationFn: ({
      speciesCategoryId,
      accounts,
    }: {
      speciesCategoryId: string;
      accounts: FixedAssetSpeciesCategoryAccountsPayload;
    }) => updateFixedAssetsSpeciesCategoryAccounts(companyId, speciesCategoryId, accounts),
    onMutate: async ({ speciesCategoryId, accounts }) => {
      const queryKey = FixedAssetsSpeciesQueryKeys.byCompany(companyId);
      await queryClient.cancelQueries({ queryKey });

      const submittedKeys = Object.keys(accounts) as (keyof FixedAssetSpeciesCategoryAccountsPayload)[];
      const current = queryClient.getQueryData<CompanyFixedAssetSpecies[]>(queryKey);
      const targetCategory = current
        ?.flatMap((companySpecies) => companySpecies.species.speciesCategories ?? [])
        .find((speciesCategory) => speciesCategory.id === speciesCategoryId);
      const previousAccounts = targetCategory
        ? (Object.fromEntries(
            submittedKeys.map((key) => [key, targetCategory[key] ?? null]),
          ) as FixedAssetSpeciesCategoryAccountsPayload)
        : undefined;

      queryClient.setQueryData<CompanyFixedAssetSpecies[]>(queryKey, (data) =>
        data?.map((companySpecies) => ({
          ...companySpecies,
          species: {
            ...companySpecies.species,
            speciesCategories: companySpecies.species.speciesCategories?.map((speciesCategory) =>
              speciesCategory.id === speciesCategoryId ? { ...speciesCategory, ...accounts } : speciesCategory,
            ),
          },
        })),
      );
      return { previousAccounts };
    },
    onError: (error, { speciesCategoryId }, context) => {
      if (context?.previousAccounts) {
        const { previousAccounts } = context;
        queryClient.setQueryData<CompanyFixedAssetSpecies[]>(FixedAssetsSpeciesQueryKeys.byCompany(companyId), (data) =>
          data?.map((companySpecies) => ({
            ...companySpecies,
            species: {
              ...companySpecies.species,
              speciesCategories: companySpecies.species.speciesCategories?.map((speciesCategory) =>
                speciesCategory.id === speciesCategoryId
                  ? { ...speciesCategory, ...previousAccounts }
                  : speciesCategory,
              ),
            },
          })),
        );
      }
      captureException(error);
      snack.error(t('accounts_save_error'));
    },
    onSuccess: () => {
      snack.success(t('accounts_save_success'));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FixedAssetsSpeciesQueryKeys.byCompany(companyId) });
    },
  });
};

export const useCreateFixedAssetsSpeciesMutation = () => {
  const companyId = useActiveCompanyId();
  const { captureException } = useExceptionLogger();
  const { t } = useTranslation();
  const { t: tModal } = useTranslation('bookkeeping', { keyPrefix: 'settings.fixed_assets.modal' });

  return useMutation({
    mutationFn: (speciesId: string) => createFixedAssetsSpecies(companyId, speciesId),
    onError: (error) => {
      captureException(error);
      snack.error(t('ENCOUNTERED_ERROR'));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FixedAssetsSpeciesQueryKeys.byCompany(companyId) });
      snack.success(tModal('create_success'));
    },
  });
};
